import React, { useState, useCallback, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import { message as antMessage } from "antd";
import { chatApi } from "../../../services/api";
import { usePageTheme } from "./theme/usePageTheme";
import type { PageThemeTokens } from "./theme/types";
import { StarFilled } from "@ant-design/icons";
import V2EndingIllustration from "./icons/V2EndingIllustration";
import V2EndingConfetti from "./icons/V2EndingConfetti";
import V2PageFrame from "./components/V2PageFrame";
import V2Header from "./components/V2Header";
import { V2_LAYOUT } from "./styles/v2Layout";

const endingPrimaryButtonStyle = (
  tokens: PageThemeTokens
): React.CSSProperties => ({
  width: "100%",
  maxWidth: 358,
  height: V2_LAYOUT.endingButtonHeight,
  borderRadius: V2_LAYOUT.endingButtonHeight / 2,
  border: "none",
  background: `linear-gradient(180deg, ${tokens.chatUserGradientStart} 0%, ${tokens.chatUserGradientEnd} 100%)`,
  boxShadow:
    "inset 0 1px 0 rgba(255, 255, 255, 0.28), 0 4px 12px rgba(0, 0, 0, 0.35)",
  color: tokens.chatUserMessageText,
  fontSize: 16,
  fontWeight: 600,
  cursor: "pointer",
});

interface UCEndingPageProps {
  csUuid?: string;
  hideHeader?: boolean;
  onBackToGame: () => void;
  onStartNewChat: () => void;
}

const UCEndingPage: React.FC<UCEndingPageProps> = ({
  csUuid,
  hideHeader = false,
  onBackToGame,
  onStartNewChat,
}) => {
  const intl = useIntl();
  const { tokens } = usePageTheme();
  const [rating, setRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [evaluationChecked, setEvaluationChecked] = useState(false);
  const submittingRef = useRef(false);
  const savedRatingRef = useRef(0);

  useEffect(() => {
    if (!csUuid) {
      setEvaluationChecked(true);
      return;
    }

    let cancelled = false;

    const loadExistingEvaluation = async () => {
      try {
        const response = await chatApi.listMemory({
          csUuid,
          pageSize: 30,
          current: 1,
        });
        if (cancelled || !response?.success || !Array.isArray(response.data)) {
          return;
        }

        const evaluatedMemory = response.data.find(
          (item: {
            type?: string;
            contentType?: string;
            content?: string;
            rating?: number;
          }) =>
            item.type === "EVALUATION" &&
            (item.contentType === "JSON" ||
              (typeof item.rating === "number" && item.rating > 0))
        );

        if (!evaluatedMemory) return;

        let existingRating = evaluatedMemory.rating;
        if (
          !existingRating &&
          evaluatedMemory.contentType === "JSON" &&
          evaluatedMemory.content
        ) {
          try {
            const parsed = JSON.parse(evaluatedMemory.content);
            existingRating = parsed?.rating;
          } catch {
            // ignore invalid evaluation payload
          }
        }

        if (typeof existingRating === "number" && existingRating > 0) {
          setRating(existingRating);
          savedRatingRef.current = existingRating;
        }
      } catch (error) {
        console.error("load evaluation status failed", error);
      } finally {
        if (!cancelled) {
          setEvaluationChecked(true);
        }
      }
    };

    loadExistingEvaluation();

    return () => {
      cancelled = true;
    };
  }, [csUuid]);

  const submitRating = useCallback(
    async (value: number) => {
      // 允许改分覆盖：后端按会话 upsert 只保留一条最终记录
      if (
        !value ||
        submittingRef.current ||
        !csUuid ||
        !evaluationChecked ||
        value === savedRatingRef.current
      ) {
        return;
      }
      const isFirstSubmit = savedRatingRef.current === 0;
      const previousRating = rating;
      submittingRef.current = true;
      setSubmitting(true);
      setRating(value);
      try {
        const response = await chatApi.submitEvaluation({
          csUuid,
          rating: value,
        });
        if (response?.success) {
          savedRatingRef.current = value;
          if (isFirstSubmit) {
            antMessage.success(
              intl.formatMessage({ id: "evaluation.submit.success" })
            );
          }
        } else {
          setRating(previousRating);
          antMessage.error(
            intl.formatMessage({ id: "evaluation.submit.error" })
          );
        }
      } catch (error) {
        setRating(previousRating);
        console.error("submit evaluation failed", error);
        antMessage.error(
          intl.formatMessage({ id: "evaluation.submit.error" })
        );
      } finally {
        submittingRef.current = false;
        setSubmitting(false);
      }
    },
    [csUuid, evaluationChecked, intl, rating]
  );

  const handleStarClick = (value: number) => {
    void submitRating(value);
  };

  return (
    <V2PageFrame
      header={
        hideHeader ? undefined : (
          <V2Header
            title={intl.formatMessage({
              id: "v2.chat_ending",
              defaultMessage: "Chat Ending",
            })}
          />
        )
      }
    >
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "24px 16px 32px",
          textAlign: "center",
        }}
      >
        <div
          style={{
            position: "relative",
            width: "100%",
            maxWidth: 358,
            height: 146,
            marginTop: 24,
            marginBottom: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <V2EndingConfetti width={358} />
          <div style={{ position: "relative", zIndex: 1, lineHeight: 0 }}>
            <V2EndingIllustration tokens={tokens} size={106} />
          </div>
        </div>

        <div
          style={{
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1.2,
            color: tokens.text,
            marginBottom: 8,
          }}
        >
          {intl.formatMessage({
            id: "v2.thank_you",
            defaultMessage: "Thank you!",
          })}
        </div>
        <div
          style={{
            fontSize: 16,
            color: tokens.textSecondary,
            marginBottom: 4,
          }}
        >
          {intl.formatMessage({
            id: "v2.glad_helped",
            defaultMessage: "We're glad to have helped you.",
          })}
        </div>
        <div
          style={{
            fontSize: 16,
            color: tokens.textSecondary,
            marginBottom: 28,
          }}
        >
          {intl.formatMessage({
            id: "v2.rate_experience",
            defaultMessage: "How was your support experience?",
          })}
        </div>

        <div
          style={{
            display: "flex",
            gap: 10,
            marginBottom: 40,
          }}
        >
          {[1, 2, 3, 4, 5].map((level) => (
            <button
              key={level}
              type="button"
              disabled={!evaluationChecked || submitting}
              onClick={() => handleStarClick(level)}
              style={{
                border: "none",
                background: "transparent",
                cursor:
                  !evaluationChecked || submitting ? "not-allowed" : "pointer",
                padding: 0,
                lineHeight: 0,
              }}
            >
              <StarFilled
                style={{
                  fontSize: 40,
                  color:
                    level <= rating
                      ? tokens.starActive
                      : tokens.starInactive,
                }}
              />
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={onBackToGame}
          style={{ ...endingPrimaryButtonStyle(tokens), marginBottom: 16 }}
        >
          {intl.formatMessage({
            id: "v2.back_to_game",
            defaultMessage: "Back to Game",
          })}
        </button>

        <button
          type="button"
          onClick={onStartNewChat}
          style={endingPrimaryButtonStyle(tokens)}
        >
          {intl.formatMessage({
            id: "v2.start_new_chat",
            defaultMessage: "Start New Chat",
          })}
        </button>
      </div>
    </V2PageFrame>
  );
};

export default UCEndingPage;
